import { validateChildOutput, type ExpectedChildOutput } from '../features/multiPackagePrint/outputManifest';
import { renderLabels } from '../wysiwyg/services/renderOrchestrator';
import { getFormat } from '../wysiwyg/formats/index';
import { defaultPreviewLayouts, loadPreviewLayouts } from '../wysiwyg/previewLayouts';
import { appConfig } from '../wysiwyg/appConfig';
import { startProgressToast, type ProgressToast } from '../components/ToastNotifications';
import { printRunRecordFilenameBase, printRunRecordPackageFilenameBase } from './printRunFilename';
import type { Template, PreviewLayout } from '../wysiwyg/types';
import { createLegacyAssetResolver } from '../wysiwyg/legacyAssets';
import { preloadNewAssets } from '../wysiwyg/legacyAssets';
import type { AssetApi } from '../wysiwyg/assetService';
import { normalizeCoaKeys } from '../wysiwyg/coaUtils';
import {
  getEffectiveShelfLifeDays,
  getUsedPrintDateVariables,
  mergePackageDateSources,
  normalizeDateOnly,
  withCalculatedPrintDates,
  withCanonicalDateAliases,
} from './printDates';
import { getLatestCoaPayload } from './coaRecords';
import { decodeIssuedPrintCoa, decodeIssuedStagedPrintCoa } from '../hooks/useIssuedPrintCoa';
import { getCOAByPackageId, getPackageById, getPackageStagedById } from '../hooks/usePackages';
import { getPrintRunById, validatePrintRunOutput } from '../hooks/usePrintRuns';
import { getProduct } from '../hooks/useProducts';
import { getAllTemplates } from '../hooks/useTemplates';
import { getFileContentUrl, getFileLink, getStaticFiles, uploadFileId } from '../hooks/useFiles';
import { DEFAULT_API_BASE_URL } from '../constants';
import { extractVariableKeys, resolveConditionalTextSource } from '../wysiwyg/textFormatter';
import { normalizeLoadedTemplate } from '../wysiwyg/templateNormalization';
import { getErrorMessage } from './requestErrors';
import { restoreSavedTacContext } from './tac';

const assetApi: AssetApi = {
  getFileId: ({ file, fileType, mimeType, organizationId }) => uploadFileId({
    file,
    fileType,
    mimeType,
    organizationId,
  }),
  getStaticFiles,
  getFileLink,
  getFileContentUrl,
};

export type PrintRunArtifact = {
  blob: Blob;
  fileName: string;
};

export type PrintRunArtifactRequest = {
  id: number;
  jobName?: string;
  jobId?: string;
  packageLabel?: string;
  packageCoaId?: number;
  expectedChildOutput?: ExpectedChildOutput;
};

export type PrintRunOutputParts = PrintRunArtifact[];

const isTemplateRecord = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

function parseTemplateData(rawData: any): Template | null {
  if (!rawData) return null;
  if (isTemplateRecord(rawData)) {
    return normalizeLoadedTemplate(rawData as Template);
  }

  try {
    let parsed = JSON.parse(rawData);
    while (typeof parsed === 'string') {
      parsed = JSON.parse(parsed);
    }
    if (!isTemplateRecord(parsed)) return null;
    return normalizeLoadedTemplate(parsed as Template);
  } catch {
    return null;
  }
}

function buildAutoLayout(tpl: Template): PreviewLayout {
  const unit = tpl.realUnit || 'in';
  const labelWidth = tpl.realWidth ?? Number((tpl.width / (tpl.dpi ?? 72)).toFixed(2));
  const labelHeight = tpl.realHeight ?? Number((tpl.height / (tpl.dpi ?? 72)).toFixed(2));

  return {
    id: 'auto-label-size',
    label: 'Match label size (1-up)',
    unit,
    pageWidth: labelWidth,
    pageHeight: labelHeight,
    labelWidth,
    labelHeight,
    marginTop: 0,
    marginRight: 0,
    marginBottom: 0,
    marginLeft: 0,
    spacingX: 0,
    spacingY: 0,
    columns: 1,
    rows: 1,
    format: 'sheets',
    printer: 'generic',
    shape: 'rectangle',
  };
}

function buildCustomLayout(tpl: Template): PreviewLayout | null {
  if (!tpl.customLayout) return null;
  const cl = tpl.customLayout;

  return {
    id: 'custom',
    label: 'Custom Layout',
    unit: cl.unit,
    pageWidth: cl.pageWidth,
    pageHeight: cl.pageHeight,
    labelWidth: cl.labelWidth ?? tpl.realWidth ?? tpl.width / (tpl.dpi ?? 72),
    labelHeight: cl.labelHeight ?? tpl.realHeight ?? tpl.height / (tpl.dpi ?? 72),
    marginTop: cl.marginTop,
    marginRight: cl.marginRight,
    marginBottom: cl.marginBottom,
    marginLeft: cl.marginLeft,
    spacingX: cl.spacingX,
    spacingY: cl.spacingY,
    columns: cl.columns,
    rows: cl.rows,
    format: cl.format || 'sheets',
    shape: cl.shape || 'rectangle',
    labelRotation: cl.labelRotation,
    printer: 'custom',
  };
}

function normalizeCoa(rawResp: any): Record<string, any> {
  const coadata: any = getLatestCoaPayload(rawResp) ?? rawResp;

  let parsedData: any = null;
  if (typeof coadata?.data === 'string') {
    try {
      parsedData = JSON.parse(coadata.data);
    } catch {
      parsedData = null;
    }
    while (typeof parsedData === 'string') {
      try {
        parsedData = JSON.parse(parsedData);
      } catch {
        break;
      }
    }
  } else if (typeof coadata?.data === 'object' && coadata.data !== null) {
    parsedData = coadata.data;
  }

  return normalizeCoaKeys({ ...(coadata || {}), ...(parsedData || {}), coaId: coadata?.id ?? coadata?.Id });
}

function getUsedTemplateBindings(template: Template): Set<string> {
  const bindings = new Set<string>();
  const collectBindings = (value: unknown) => {
    if (typeof value === 'string') {
      extractVariableKeys(value).forEach((key) => bindings.add(key));
    } else if (Array.isArray(value)) value.forEach((entry) => collectBindings(entry));
    else if (value && typeof value === 'object') {
      const node = value as { type?: unknown; text?: unknown; variableKey?: unknown; operator?: unknown };
      if (node.type === 'text') {
        extractVariableKeys(resolveConditionalTextSource(undefined, node.text,
          typeof node.variableKey === 'string' ? node.variableKey : undefined))
          .forEach((key) => bindings.add(key));
      }
      if (typeof node.operator === 'string' && typeof node.variableKey === 'string') bindings.add(node.variableKey);
      Object.entries(value).forEach(([key, entry]) => {
        if (key !== 'variableKey') collectBindings(entry);
      });
    }
  };
  collectBindings(template.elements);
  return bindings;
}

// Generated date controls render their saved theme fallback. Legacy raw bindings
// may instead need a saved confirmation; current shelf life cannot recreate history.
function resolveIssuedDownloadDates(
  coa: Record<string, any>, template: Template, packagedDate: string, shelfLifeDays?: number,
): Record<string, any> {
  const result = withCanonicalDateAliases(coa);
  const bindings = getUsedTemplateBindings(template);
  const controlledKinds = new Set(getUsedPrintDateVariables(template).map((variable) => variable.dateKind));
  for (const kind of ['expirationDate', 'bestByDate'] as const) {
    const source = result[kind];
    if (!bindings.has(`coa.${kind}`) || controlledKinds.has(kind)
      || !(source == null || (typeof source === 'string' && !source.trim()))) continue;
    const confirmations = Array.isArray(template.confirmedDates) ? template.confirmedDates : [];
    const confirmed = confirmations.find((date) => date?.kind === kind && date.usedByTemplate === true);
    const savedValue = normalizeDateOnly(confirmed?.confirmedDate);
    if (confirmed?.isExplicitlyBlank === true || savedValue) {
      result[kind] = confirmed?.isExplicitlyBlank === true ? '' : savedValue;
      continue;
    }
    const formerlyCalculated = withCalculatedPrintDates(result, packagedDate, shelfLifeDays);
    if (normalizeDateOnly(formerlyCalculated[kind])) {
      const label = kind === 'expirationDate' ? 'expiration' : 'best-by';
      throw new Error(`The original ${label} date cannot be recovered safely because its calculated historical value was not saved.`);
    }
  }
  return result;
}

async function resolveLayout(template: Template): Promise<PreviewLayout> {
  const saved = (template as Template & { multiPackageLayoutSnapshot?: PreviewLayout }).multiPackageLayoutSnapshot;
  if (saved) return saved;
  const autoLayout = buildAutoLayout(template);
  const customLayout = buildCustomLayout(template);

  const desiredLayoutId = template.selectedLayoutId || template.defaultLayoutId;
  if (desiredLayoutId === 'custom') {
    return customLayout ?? autoLayout;
  }
  if (desiredLayoutId) {
    const allLayouts = await loadPreviewLayouts(appConfig.previewLayoutsEndpoint, defaultPreviewLayouts);
    const matched = allLayouts.find((layout) => layout.id === desiredLayoutId);
    if (matched) return matched;
  }
  if (customLayout) return customLayout;

  return autoLayout;
}

async function resolveCommonData(organizationId: number, job: any, template: Template, requestedRunId: unknown) {
  let packageData: any = null;
  const printRun = job;

  if (printRun.packageId) {
    packageData = await getPackageById(organizationId, printRun.packageId).catch(() => null);
  }
  const pkg = packageData?.result || packageData || {};

  const isDraft = printRun.status === 4 || printRun.status === 'Draft';
  const hasIssuedUnits = !isDraft && Array.isArray(printRun.qrs) && printRun.qrs.length > 0;
  const hasCoaRelationship = printRun.packageId != null || printRun.stagePackageCoaId != null
    || printRun.stagedPrintContext != null || (typeof printRun.packageLabel === 'string' && Boolean(printRun.packageLabel.trim()))
    || [...getUsedTemplateBindings(template)].some((key) => key === 'coa' || key.startsWith('coa.'));
  const usesIssuedSnapshot = printRun.outputCoaSnapshot != null || (hasIssuedUnits && hasCoaRelationship);
  let coaData: Record<string, any> = {};
  if (usesIssuedSnapshot) {
    const snapshot = printRun.packageId != null
      ? decodeIssuedPrintCoa(printRun, requestedRunId, printRun.packageId)
      : decodeIssuedStagedPrintCoa(printRun, requestedRunId);
    if (!snapshot) throw new Error('The issued print COA snapshot is unavailable or does not match this print run.');
    coaData = snapshot;
  } else if (printRun.packageId) {
    const rawCoa = await getCOAByPackageId(organizationId, printRun.packageId).catch(() => null);
    const unwrapped = rawCoa?.result ?? rawCoa;
    const records = Array.isArray(unwrapped) ? unwrapped : unwrapped ? [unwrapped] : [];
    const runCoa = printRun.packageCoaId
      ? records.find((coa: any) => Number(coa.id ?? coa.Id) === Number(printRun.packageCoaId))
      : null;
    if (printRun.packageCoaId && !runCoa) {
      throw new Error('Original COA for this print run is unavailable');
    }
    if (runCoa || rawCoa) {
      coaData = normalizeCoa(runCoa ?? rawCoa);
      if (printRun.packageCoaId && coaData.packageTag
        && coaData.packageTag !== printRun.packageLabel) {
        throw new Error('Original COA does not belong to this child print run');
      }
    }
  } else if (printRun.packageLabel) {
    const stagedPkg = await getPackageStagedById(organizationId, printRun.packageLabel).catch(() => null);
    if (stagedPkg) {
      // staged/byid returns stagedCoaData; `data` is only a legacy response shape.
      let parsedData: any = stagedPkg.stagedCoaData ?? stagedPkg.data ?? null;
      if (typeof parsedData === 'string') {
        try { parsedData = JSON.parse(parsedData); } catch { /* ignore */ }
      }
      while (typeof parsedData === 'string') {
        try { parsedData = JSON.parse(parsedData); } catch { break; }
      }
      if (!parsedData || typeof parsedData !== 'object' || Array.isArray(parsedData)) parsedData = null;
      const stagedCoaId = [printRun.stagePackageCoaId, stagedPkg.stagedCoaId]
        .map((value) => Number(value))
        .find((value) => Number.isSafeInteger(value) && value > 0) ?? null;
      coaData = normalizeCoaKeys({ ...(parsedData || {}), coaId: stagedCoaId });
    }
  }

  const overrideProductId = printRun.productOverride ?? printRun.productOverrideId;
  const productId = overrideProductId ?? printRun.productId ?? pkg.productId ?? pkg.ProductId;
  let productData: Record<string, any> | null = null;
  if (printRun.multiPackageProductSnapshot != null) {
    try {
      const snapshot = typeof printRun.multiPackageProductSnapshot === 'string'
        ? JSON.parse(printRun.multiPackageProductSnapshot)
        : printRun.multiPackageProductSnapshot;
      if (!isTemplateRecord(snapshot) || Number(snapshot.id) !== Number(productId)) {
        throw new Error('Original product snapshot does not match this child print run');
      }
      productData = snapshot;
    } catch {
      throw new Error('Original product snapshot for this child print run is unavailable');
    }
  } else if (printRun.sourceName === 'Multi-Package Print') {
    throw new Error('Original product snapshot for this child print run is unavailable');
  } else if (productId) {
    productData = await getProduct(organizationId, Number(productId)).catch(() => null);
  }

  const count = Number((printRun.count ?? printRun.Count ?? (printRun.qrs || []).length) || 1);
  const childRun = printRun.sourceName === 'Multi-Package Print';
  if (childRun && (!printRun.packageCoaId || Number(coaData.coaId) !== Number(printRun.packageCoaId) || !coaData.packageTag
    || coaData.packageTag !== printRun.packageLabel)) {
    throw new Error('Original child COA and package identity are unavailable');
  }
  const packageTag = childRun ? coaData.packageTag : printRun.packageLabel || pkg.label || coaData.packageTag || '';
  const qrValues = Array.isArray(printRun.qrs) && printRun.qrs.length > 0
    ? printRun.qrs
    : Array.from({ length: count }, () => packageTag);
  if (printRun.sourceName === 'Multi-Package Print'
    && (!Array.isArray(printRun.qrs) || printRun.qrs.length !== count)) {
    throw new Error('Original child QR range is unavailable');
  }

  const resolvedProductName =
    (printRun.sourceName === 'Multi-Package Print' ? null : printRun.productName) ||
    (printRun.sourceName === 'Multi-Package Print' ? null : printRun.product) ||
    productData?.name ||
    coaData.productName ||
    pkg.productName ||
    '';
  const normalizedPackageDates = withCanonicalDateAliases(normalizeCoaKeys(pkg));
  const packageDateSources = {
    rawPackageDates: normalizedPackageDates.rawPackageDates,
    packagedDate: normalizedPackageDates.packagedDate,
    useByDate: normalizedPackageDates.useByDate,
    sellByDate: normalizedPackageDates.sellByDate,
    shelfLifeDate: normalizedPackageDates.shelfLifeDate,
    expirationDate: normalizedPackageDates.expirationDate,
    bestByDate: normalizedPackageDates.bestByDate,
    harvestDate: normalizedPackageDates.harvestDate,
    cultivationDate: normalizedPackageDates.cultivationDate,
  };
  const coaDateData = printRun.packageId && !usesIssuedSnapshot
    ? mergePackageDateSources(coaData, packageDateSources)
    : coaData;

  let legacyImages: Record<string, string> = {};
  let legacyFonts: Record<string, string> = {};
  let newAssetImages: Record<string, string> = {};
  let newAssetFonts: Record<string, string> = {};

  const getToken = () => localStorage.getItem('rid.jwt');

  try {
    const legacyResolver = createLegacyAssetResolver({
      apiBaseUrl: DEFAULT_API_BASE_URL,
      organizationId: String(organizationId),
      getToken,
    });
    const legacyResult = await legacyResolver.preloadTemplateAssets(template);
    legacyImages = legacyResult.images;
    legacyFonts = legacyResult.fonts;
  } catch (err) {
    console.warn('[downloadPrintRunOutput] Failed to preload legacy assets:', err);
  }

  try {
    const newResult = await preloadNewAssets(template, assetApi, organizationId, getToken);
    newAssetImages = newResult.images;
    newAssetFonts = newResult.fonts;
  } catch (err) {
    console.warn('[downloadPrintRunOutput] Failed to preload new assets:', err);
  }

  return {
    pkg,
    coaData,
    coaDateData,
    usesIssuedSnapshot,
    productData,
    count,
    packageTag,
    qrValues,
    resolvedProductName,
    legacyImages,
    legacyFonts,
    newAssetImages,
    newAssetFonts,
    printRun,
  };
}

export const createPrintRunOutputArtifacts = async (
  organizationId: number,
  job: PrintRunArtifactRequest,
  progress?: ProgressToast,
): Promise<PrintRunOutputParts> => {
  const jobLabel = job?.jobName || 'Print Run';
  const processingLabel = `Processing Download for ${jobLabel}...`;
  await validatePrintRunOutput(organizationId, Number(job.id));
  progress?.report(0.05, processingLabel);
    const rawPrintRun = await getPrintRunById(organizationId, job.id);
    const printRun = rawPrintRun;
    validateChildOutput(organizationId, job, printRun);

    if (!printRun) {
      throw new Error('Print run not found');
    }
    const requestedRunId = Number(job.id);
    if (!Number.isSafeInteger(requestedRunId) || requestedRunId <= 0 || Number(printRun.id) !== requestedRunId) {
      throw new Error('The print run response does not match the requested print run.');
    }
    if (job.packageCoaId != null && Number(printRun.packageCoaId) !== Number(job.packageCoaId)) {
      throw new Error('Print run COA does not match the completed child output manifest');
    }
    const expectsChildManifest = job.packageCoaId != null || printRun.sourceName === 'Multi-Package Print';
    if (expectsChildManifest && job.packageLabel && printRun.packageLabel !== job.packageLabel) {
      throw new Error('Print run package does not match the completed child output manifest');
    }

    progress?.report(0.1, processingLabel);
    let template = parseTemplateData(printRun.labelTemplateData || printRun.templateData || printRun.template);

    if (!template && printRun.sourceName !== 'Multi-Package Print' && printRun.templateId != null) {
      const templates = await getAllTemplates({ organizationId, page: 0, pageSize: 500 });
      const selectedTemplate = (templates || []).find((t: any) => Number(t.id) === Number(printRun.templateId));
      template = parseTemplateData(selectedTemplate?.content || selectedTemplate?.data);
    }

    if (!template) {
      throw new Error('Template data for this print run is unavailable');
    }

    progress?.report(0.2, processingLabel);
    const selectedLayout = await resolveLayout(template);
    progress?.report(0.3, processingLabel);
    const resolved = await resolveCommonData(organizationId, printRun, template, requestedRunId);
    const {
      pkg, coaData, coaDateData, usesIssuedSnapshot, productData, count, packageTag,
      qrValues, resolvedProductName,
      legacyImages, legacyFonts, newAssetImages, newAssetFonts,
    } = resolved;

    const format = template.outputFormat ?? 'pdf';
    const filenameBase = format === 'pdf'
      ? printRunRecordPackageFilenameBase(printRun, job, pkg.label || coaData.packageTag || packageTag)
      : printRunRecordFilenameBase(printRun, job);

    const canonicalCoaDateData = withCanonicalDateAliases(coaDateData);
    const outputCoa = usesIssuedSnapshot ? resolveIssuedDownloadDates(
      canonicalCoaDateData, template, printRun.packagedDate || canonicalCoaDateData.packagedDate || '',
      getEffectiveShelfLifeDays(productData?.shelfLifeDays, template.shelfLifeDays),
    ) : withCalculatedPrintDates(
      canonicalCoaDateData,
      printRun.packagedDate || pkg.packagedDate || coaData.packagedDate || '',
      getEffectiveShelfLifeDays(productData?.shelfLifeDays, template.shelfLifeDays),
      getUsedPrintDateVariables(template),
    );
    const renderCoa = restoreSavedTacContext(outputCoa, template, printRun, organizationId);

    const data = {
      common: {
        packageId: printRun.packageId || pkg.id,
        packageTag,
        product: resolvedProductName,
        market: coaData.market || '',
        facility: coaData.facility || '',
        packagedDate: printRun.sourceName === 'Multi-Package Print'
          ? (template as Template & { multiPackagePackagedDate?: string }).multiPackagePackagedDate
            ?? printRun.packagedDate ?? coaData.packagedDate ?? ''
          : printRun.packagedDate || (usesIssuedSnapshot
            ? outputCoa.packagedDate
            : pkg.packagedDate || coaData.packagedDate) || '',
        printDate: (template as Template & { multiPackagePrintDate?: string }).multiPackagePrintDate
          ?? printRun.printedDate ?? printRun.printDate ?? '',
        count,
        jobId: printRun.jobId || '',
        jobName: printRun.jobName || '',
      },
      job: printRun,
      coa: {
        ...renderCoa,
        productName: resolvedProductName,
        ProductName: resolvedProductName,
      },
      product: productData,
      qrValues,
    };

    const reelSettings = (template as Template & { draftPrintSettings?: { splitIntoReels?: boolean; labelsPerReel?: number } }).draftPrintSettings;
    const labelsPerReel = Number(reelSettings?.labelsPerReel);
    const splitIntoReels = reelSettings?.splitIntoReels === true;
    if (splitIntoReels && (format !== 'pdf' || !Number.isSafeInteger(labelsPerReel) || labelsPerReel <= 0)) {
      throw new Error('Reel settings require PDF output and a positive whole labels-per-reel value');
    }

    const formatDef = getFormat(format);

    const stopAnimating = progress?.animateTo(0.9, processingLabel) ?? (() => {});
    try {
      const parts: PrintRunArtifact[] = [];
      let totalArtifactBytes = 0;
      const size = splitIntoReels ? labelsPerReel : count;
      for (let start = 0; start < count; start += size) {
        const reelCount = Math.min(size, count - start);
        const result = await renderLabels({
          template,
          layout: selectedLayout,
          labelCount: reelCount,
          qrIndexOffset: printRun.sourceName === 'Multi-Package Print' ? start : undefined,
          topOffset: template.topOffset ?? 0,
          leftOffset: template.leftOffset ?? 0,
          rotation: template.pageRotation ?? 0,
          data: printRun.sourceName === 'Multi-Package Print'
            ? data
            : { ...data, common: { ...data.common, count: reelCount }, qrValues: qrValues.slice(start, start + reelCount) },
          legacyImages,
          legacyFonts,
          newAssetImages,
          newAssetFonts,
          isPreview: false,
          hasCoa: usesIssuedSnapshot || Boolean(coaData.coaId),
        });
        const revokeUrl = formatDef.revokeUrl ?? URL.revokeObjectURL;
        try {
          const response = await fetch(result.url);
          if (!response.ok) throw new Error('Rendered print run output could not be read');
          const blob = await response.blob();
          totalArtifactBytes += blob.size;
          if (splitIntoReels && totalArtifactBytes > 64 * 1024 * 1024)
            throw new Error('Reel ZIP exceeds the browser download limit');
          parts.push({
            blob,
            fileName: `${filenameBase}${splitIntoReels ? `-reel-${parts.length + 1}` : ''}.${formatDef.fileExtension}`,
          });
        } finally {
          revokeUrl(result.url);
        }
      }
      return parts;
    } finally {
      stopAnimating();
    }
};

export const createPrintRunOutputArtifact = async (
  organizationId: number, job: PrintRunArtifactRequest, progress?: ProgressToast,
): Promise<PrintRunArtifact> => {
  const parts = await createPrintRunOutputArtifacts(organizationId, job, progress);
  if (parts.length !== 1) throw new Error('This print run has multiple reels; download the package ZIP instead');
  return parts[0];
};

const downloadPrintRunOutput = async (organizationId: number, job: PrintRunArtifactRequest, existingToast?: ProgressToast) => {
  const jobLabel = job?.jobName || 'Print Run';
  const processingLabel = `Processing Download for ${jobLabel}...`;
  const progress = existingToast ?? startProgressToast(processingLabel);

  try {
    const artifacts = await createPrintRunOutputArtifacts(organizationId, job, progress);
    let artifact = artifacts[0];
    if (artifacts.length > 1) {
      const { createBoundedZip, MAX_BROWSER_ZIP_BYTES } = await import('./boundedZip');
      const files: Record<string, Uint8Array> = {};
      if (artifacts.reduce((sum, part) => sum + part.blob.size, 0) > MAX_BROWSER_ZIP_BYTES)
        throw new Error('Reel ZIP exceeds the browser download limit');
      for (const part of artifacts) files[part.fileName] = new Uint8Array(await part.blob.arrayBuffer());
      artifact = { blob: new Blob([new Uint8Array(await createBoundedZip(files)).buffer]), fileName: `${artifacts[0].fileName.replace(/-reel-1\.pdf$/i, '')}-reels.zip` };
    }
    if (!artifact) throw new Error('Original output is unavailable');
    progress.report(0.95, processingLabel);
    const url = URL.createObjectURL(artifact.blob);
    const downloadLink = document.createElement('a');
    downloadLink.href = url;
    downloadLink.download = artifact.fileName;
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
    setTimeout(() => URL.revokeObjectURL(url), 1000);

    progress.succeed(`Download ready for ${jobLabel}`);
  } catch (error: any) {
    console.error('[downloadPrintRunOutput] Failed to generate print run output:', error);
    progress.fail(`The print run output could not be downloaded: ${getErrorMessage(error, 'please try again.')}`);
  }
};

export { resolveLayout as resolvePrintRunLayout };
export default downloadPrintRunOutput;
